import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";

const eur = (n) => `€${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;

// Doppia conferma: 1) riepilogo con i conteggi, 2) digitare il nome esatto dell'anno
export const DeleteYearDialog = ({ year, open, onOpenChange, onDeleted }) => {
  const [info, setInfo] = useState(null);
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !year) return;
    setInfo(null); setStep(1); setTyped("");
    api.get("/admin/years/delete-preview", { params: { year } }).then((r) => setInfo(r.data))
      .catch((err) => { toast.error(apiError(err.response?.data?.detail)); onOpenChange(false); });
  }, [open, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/admin/years", { params: { year, confirm: typed } });
      toast.success(`Anno ${year} eliminato`); onDeleted(data); onOpenChange(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const rows = info ? [
    ["Configurazione dell'anno (prezzi, corsi, date, codice)", 1],
    ["Iscrizioni", info.enrollments], ["di cui portate da un altro anno", info.carried_enrollments],
    ["Registro dei passaggi allievi", info.carry_logs],
    ["Risposte di preiscrizione", info.prereg_answers], ["Usciti dal corso", info.prereg_exits],
    ["Registro uscite dal corso", info.prereg_logs], ["Moduli firmati", info.signed_forms],
    ["Pagamenti", `${info.payments} (incassato ${eur(info.paid_total)})`],
    ["Lezioni del calendario", info.lessons], ["Chiusure", info.closures],
    ["Date di prova", info.trial_slots], ["Prenotazioni di prova", info.trial_bookings],
    ["Modulo, privacy e notifiche dell'anno", info.year_settings],
    ["Docenti, saggi e blocchi dei corsi", info.course_meta],
  ] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-year-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Elimina anno {year}</DialogTitle>
          <DialogDescription>Si eliminano solo i dati di questo anno. Gli account degli utenti restano.</DialogDescription>
        </DialogHeader>
        {!info ? <p className="text-sm text-muted-foreground">Calcolo…</p> : step === 1 ? (
          <div className="space-y-3">
            {info.payments > 0 && (
              <div data-testid="delete-year-payments-warning" className="rounded-lg border-2 border-destructive bg-destructive/10 p-3 text-sm font-semibold text-destructive flex gap-2">
                <AlertTriangle size={18} className="shrink-0" /> Ci sono pagamenti registrati: scarica prima il backup (Notifiche → Backup).
              </div>
            )}
            <ul className="text-sm divide-y divide-border rounded-lg border border-border" data-testid="delete-year-counts">
              {rows.map(([l, v]) => <li key={l} className={`flex justify-between gap-3 p-2 ${l.startsWith("di cui") ? "pl-6 text-muted-foreground" : ""}`}><span>{l}</span><strong className="shrink-0">{v}</strong></li>)}
            </ul>
            <p className="text-xs text-muted-foreground" data-testid="delete-year-trials-note">Vengono eliminate solo le date di prova e le prenotazioni assegnate a questo anno. Gli account degli utenti restano.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Label className="text-sm">Per confermare digita il nome esatto dell'anno: <strong>{year}</strong></Label>
            <Input data-testid="delete-year-typed" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={year} className="bg-white" autoComplete="off" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          {step === 1
            ? <Button data-testid="delete-year-next" variant="destructive" disabled={!info} onClick={() => setStep(2)}>Continua</Button>
            : <Button data-testid="delete-year-confirm" variant="destructive" disabled={busy || typed !== year} onClick={del}><Trash2 size={16} className="mr-1.5" /> Elimina definitivamente</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
